/* ═══════════════════════════════════════════════════════════════
   Forma zone drag — the indicators (shared by every forma editor's sheet —
   Forma Studio and the project editor — and the DropZone chip lists).

   Everything the slot targeting shows, drawn from the geometry
   `useZoneSlotDrag` reports: the accent caret in the opened gap, the
   Replace ring + label, the deliberate "New line" bands above the first /
   below the last line, and the "Add to …" hint on an empty column.
   Kit classes only (see the .dsg-slot-* rules in editor.css).
   ═══════════════════════════════════════════════════════════════ */

import type { SlotUi } from './useZoneSlotDrag';

export interface SlotLayerColumn {
  zoneKey: string;
  /** CSS left / width of the column inside the root (e.g. '33.333%'). */
  left: string;
  width: string;
  /** Has tokens — gets the New line bands. Empty columns get the hint instead. */
  hasTokens: boolean;
}

export function SlotDropLayer({
  ui,
  dragging,
  columns,
}: {
  ui: SlotUi | null;
  dragging: boolean;
  columns: SlotLayerColumn[];
}) {
  if (!dragging) return null;
  const t = ui?.target;
  return (
    <>
      {columns.map((c) => {
        const over = (side: 'above' | 'below') =>
          t?.zoneKey === c.zoneKey && t.kind === `newline-${side}`;
        if (!c.hasTokens) {
          const showEmpty = t?.zoneKey === c.zoneKey && t.kind === 'empty';
          return (
            <div
              key={c.zoneKey}
              className={`dsg-slot-empty${showEmpty ? ' is-over' : ''}`}
              style={{ left: c.left, width: c.width }}
              aria-hidden="true"
            >
              {showEmpty && <span className="dsg-slot-label">{ui?.label}</span>}
            </div>
          );
        }
        return (['above', 'below'] as const).map((side) => (
          <div
            key={`${c.zoneKey}-${side}`}
            className={`dsg-slot-newline dsg-slot-newline-${side}${over(side) ? ' is-over' : ''}`}
            style={{ left: c.left, width: c.width }}
            data-slot-newline={side}
            data-slot-zone={c.zoneKey}
            aria-hidden="true"
          >
            {over(side) && <span className="dsg-slot-label">New line</span>}
          </div>
        ));
      })}
      {ui?.caret && (
        <span
          className="dsg-slot-caret"
          aria-hidden="true"
          style={{ left: ui.caret.left, top: ui.caret.top, height: ui.caret.height }}
        />
      )}
      {ui?.ring && (
        <span
          className="dsg-slot-ring"
          aria-hidden="true"
          style={{
            left: ui.ring.left,
            top: ui.ring.top,
            width: ui.ring.width,
            height: ui.ring.height,
          }}
        >
          <span className="dsg-slot-label">Replace</span>
        </span>
      )}
    </>
  );
}
